import * as React from 'react';

import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Button, Checkbox, cn, TextField } from '@rematter/ui-web';
import { LucideGripVertical, LucideTrash2 } from 'lucide-react';

import type { PhotoRequirement } from '../bookings/BookingPhotoRequirements';

import { useTranslations } from '~/modules/trade/i18n/useTranslations';

interface PhotoRequirementRowProps {
  requirement: PhotoRequirement;
  index: number;
  onUpdate: (
    index: number,
    field: keyof Pick<PhotoRequirement, 'description' | 'required'>,
    value: string | boolean,
  ) => void;
  onRemove: (index: number) => void;
  validationError?: string;
  isDisabled?: boolean;
  onFocus?: () => void;
}

const PhotoRequirementRowComponent: React.FC<PhotoRequirementRowProps> = ({
  requirement,
  index,
  onUpdate,
  onRemove,
  validationError,
  isDisabled,
  onFocus,
}) => {
  const t = useTranslations();

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: requirement.id,
    disabled: isDisabled,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        '@PhotoRequirementRowComponent',
        'gap-2',
        isDragging && 'opacity-50',
        'flex items-center',
        validationError && 'items-start',
      )}
      data-photo-requirement-index={index}
    >
      <div
        className={cn(
          'flex h-10 w-14 items-center rounded-lg px-1.5',
          isDisabled && 'cursor-not-allowed bg-neutral/10 text-neutral/40',
          !isDisabled && 'cursor-move bg-neutral/5 text-neutral/60',
        )}
        {...(isDisabled ? {} : { ...attributes, ...listeners })}
      >
        <LucideGripVertical className="size-4" />
        <div className="w-7 text-center text-sm">#{index + 1}</div>
      </div>

      <TextField
        placeholder={t.bookingsView.form.placeholders.photoInstructionHere}
        value={requirement.description}
        onChange={(value) => onUpdate(index, 'description', value)}
        onFocus={() => onFocus?.()}
        className="min-w-82 flex-1"
        isInvalid={!!validationError}
        errorMessage={validationError}
        isDisabled={isDisabled}
      />

      <div className="flex h-[2.5rem] w-[6.75rem] items-center justify-center rounded-lg border border-neutral/20 px-3 py-2">
        <Checkbox
          isSelected={requirement.required}
          onChange={(checked) => onUpdate(index, 'required', checked)}
          isDisabled={isDisabled}
        >
          {t.bookingsView.form.labels.required}
        </Checkbox>
      </div>

      <Button
        variant="outline"
        size="icon"
        color="failure"
        onPress={() => onRemove(index)}
        className="size-10 rounded-lg"
        icon={<LucideTrash2 className="size-4" />}
        isDisabled={isDisabled}
      />
    </div>
  );
};

export const PhotoRequirementRow = React.memo(PhotoRequirementRowComponent);
